

import { Component, Vue, Prop } from "vue-property-decorator"
import { namespace, Getter, Action } from "vuex-class"

// @ts-ignore
import cloneDeep from "lodash/cloneDeep"

import { Team, FieldInstance, DataDefinitionWithFields } from "@/types"
import User, { CurrentUser, UserWithFields } from "@/types/user"
import { Subscription } from "@/types/subscription"

import api from "@/services/api"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "@/components/Config/DataTable.vue"
import UserImage from "@/components/UserImage.vue"
import { formatDateMixin } from "@/mixins/formatDateMixin"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"

const setup = namespace("setup")

@Component({
	components: {
		"skeleton": SkeletonLoader,
		"scrollbar": VuePerfectScrollbar,
		"data-table": DataTable,
		"user-image": UserImage
	}
})
export default class UserList extends Vue {
	@Prop() public team!: Team | null;

	@Getter("getMe") public me!: CurrentUser;

	@Getter("getSubscription") subscription?: Subscription
	@Getter("hasSubscription") hasSubscription!: boolean
	@Action("loadSubscription") loadSubscription!: () => Promise<void>
	@Getter("getIsSuiteEnabled") isSuiteEnabled! : boolean;

	@setup.Getter("getTeams") public teams!: Team[];

	@setup.Action public getADEnabled!: () => Promise<boolean>

	public filter: string = "";

	public users: UserWithFields[] = [];
	public allUsers: UserWithFields[] = [];

	private editUser: UserWithFields | null = null;

	public loading: boolean = true;
	public adEnabled: boolean = false;

	private errorEmailValue: string | null = null;
	private emailStatus: { [key: string]: boolean } = {};

	private userDefinition: DataDefinitionWithFields | null = null;

	/**
	 * On mount, get users page.
	 */
	private async mounted (): Promise<void> {
		this.loading = true

		await this.reloadData()
		this.getADEnabled().then(enabled => this.adEnabled = enabled)

		this.loading = false
	}

	/**
	 * Checks for exceeding the maximum limit of users in subscription.
	 */
	public get canCreateUser (): boolean {
		return !!(this.subscription && this.subscription.casesUsers < this.subscription.casesUserLimit)
	}

	/**
	 * Checks if is possible to display a warning about changing a user's
	 * account subscription limit when creating a new user.
	 */
	public get canDisplayCreateWarning (): boolean {
		return !!(this.hasSubscription && !this.editUser)
	}

	/**
	 * Checks if is possible to display a warning about changing a user's
	 * account subscription limit when deleting a user.
	 */
	public get canDisplayDeleteWarning (): boolean {
		return !!(this.hasSubscription && this.editUser)
	}

	/**
	 * List of columns for a table
	 */
	public get columns () {
		const defaultColumns = [
			{
				key: "image",
				hasTemplate: true,
				inModal: () => false
			},

			{
				key: "name",
				label: "Name",
				sortable: true,
				readonly: this.isSuiteEnabled,
				errors: (item: any) => item.name ? null : "Please enter a name"
			},

			{
				key: "email",
				label: "Email",
				sortable: true,
				readonly: (this.editUser && this.editUser.id) || this.isSuiteEnabled,
				errors: (item: any) => {
					if (this.editUser &&
						this.editUser.id &&
						this.editUser.email === item.email
					) {
						return null
					}

					if (!item.email) return "Please enter an email address"

					const regx = /\S+@\S+\.\S+/
					if (!regx.test(String(item.email).toLocaleLowerCase())) {
						return "Please enter a valid email address"
					}

					if (this.emailStatus[item.email]) {
						return "This email address is already in use"
					}

					api.emailExists(item.email).then(exists => {
						this.emailStatus[item.email] = exists
						if (exists) this.errorEmailValue = item.email
					})

					return null
				},
				status: this.emailStatus,
				errorValue: this.errorEmailValue
			},

			{
				key: "teams",
				hasTemplate: true,
				label: "Teams",
				dataType: "list-multiple",
				valueColumn: "userTeams",
				options: this.teams,
				readonly: this.isSuiteEnabled
			},

			{
				key: "pictureUrl",
				label: "Picture",
				inTable: false,
				tooltip: "Provide a URL to a location where the image is stored for this user."
			},

			{
				key: "disabled",
				label: "Account Disabled",
				dataType: "checkbox",
				inTable: true,
				hasTemplate: true,
				inModal: (item: User) => item.id !== this.me.id,
				tooltip: "Deactivate this account.  This user will no longer be able to log in to Cases, and will no longer be able to be added to a record.",
				readonly: this.isSuiteEnabled
			},

			{
				key: "adAccount",
				label: "Active Directory Account",
				inTable: false,
				inModal: (item: any) => this.adEnabled,
				readonly: this.editUser && this.editUser.id
			}
		]

		let allColumns = defaultColumns

		if (this.userFields.length > 0) {
			const fieldColumns : any[] = this.userFields.map(c => {
				return {
					key: "fieldValues." + c.field.id! + ".value",
					labelKeyPath: "fieldValues." + c.field.id! + ".label",
					label: c.field.title,
					dataType: c.field.dataType,
					hasTemplate: true,
					dataSourceId: c.field.dataSourceId,
					inTable: c.includeInTable,
					sortable: true,
					field: c.field,
					inModal: (item: any) => true
				}
			})
			allColumns = allColumns.concat(fieldColumns)
		}

		return allColumns
	}

	public get userFields () : FieldInstance[] {
		if (this.userDefinition && this.userDefinition.fields) {
			return this.userDefinition.fields
		}
		return []
	}

	/**
	 * Loads a list of users for further display in a table
	 */
	private async loadUsers (): Promise<void> {
		this.allUsers = await api.usersGet(null)
		this.users = await api.usersGet(this.team ? this.team.id : null)
	}

	/**
	 * Reloads data for users as well as subscription
	 * after creating or deleting a user
	 */
	private async reloadData (): Promise<void> {
		try {
			await Promise.all([
				this.loadUsers(),
				this.loadSubscription(),
				this.loadUserDefinition()
			])
		} catch (err) {
			this.$bvToast.toast(err, {
				title: "Error reload data",
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}
	}

	public async loadUserDefinition () {
		this.userDefinition = await api.userSpecialDefinition()
	}

	/**
	 * Loads full information about the selected user for further editing
	 */
	public async loadUserDetails (item: UserWithFields): Promise<UserWithFields> {
		const itemClone = cloneDeep(item)
		Vue.set(
			itemClone,
			"userTeams",
			this.teams.filter(t => item.teams.some(ut => ut.id === t.id))
		)

		if (this.me && this.me.isAdmin) {
			const accountDetails = await api.accountDetails(itemClone.id)
			Vue.set(
				itemClone,
				"account",
				accountDetails
			)
		}

		this.editUser = itemClone
		this.emailStatus = {}

		return itemClone
	}

	/**
	 * Creates an empty model for creating a new user via a modal
	 */
	public createNewItem (): { teams: never[]; userTeams: (Team | undefined)[] } {
		this.emailStatus = {}
		this.editUser = null

		return {
			teams: [],
			userTeams: this.team ? [this.teams.find(t => t.id === this.team!.id)] : []
		}
	}

	/**
	 * Create and update a user
	 */
	public async saveItem (user: UserWithFields): Promise<void> {
		// Only for new users, if there is a subscription, and the subscription has no more space
		if (!user.id && this.hasSubscription && !this.canCreateUser) {
			this.$bvToast.toast(
				"Subscription limit has been exceeded. User cannot be created.", {
					title: "Error",
					variant: "danger",
					solid: true,
					autoHideDelay: 10000
				}
			)
			return
		}

		user.teams = user.userTeams ? user.userTeams.map((team: any) => {
			if (team.id) {
				return {
					id: team.id,
					title: team.title
				}
			} else {
				const teamDetails = this.teams.find(f => f.id === team)
				if (teamDetails) {
					return {
						id: teamDetails.id,
						title: teamDetails.title
					}
				} else {
					return null
				}
			}
		}).filter((team: any) => team) : []

		try {
			await api.userSave(user)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error saving user: ${user.name}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		this.editUser = null

		await this.reloadData()
	}

	/**
	 * Remove a user
	 */
	public async deleteItem (field: User): Promise<void> {
		try {
			await api.userDelete(field.id)
		} catch (err) {
			this.$bvToast.toast(err, {
				title: `Error deleting user: ${field.name}`,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})
		}

		this.editUser = null
		this.emailStatus = {}

		await this.reloadData()
	}

	/**
	 * Enters to editing mode
	 */
	public startEdit (): void {
		this.$emit("editTeam")
	}

	private userFieldSlotName (userField: FieldInstance) : string {
		return "column.fieldValues." + userField.field.id! + ".value"
	}

	private getDisplayLabel (dataItem: any, userField: FieldInstance) {
		if (!dataItem.fieldValues || !dataItem.fieldValues[userField.field.id!]) {
			return ""
		}

		const labeledValue = dataItem.fieldValues[userField.field.id!]

		const dataType = userField.field.dataType!

		const displayLabel = formatFieldsMixin.methods.getDisplayLabelFromLabeledValue(labeledValue, dataType)
		return displayLabel
	}
}

